import * as React from "react"
import {
    DragDropContext,
    Draggable,
    DragStart,
    DragUpdate,
    Droppable,
    DropResult,
} from "react-beautiful-dnd"

import { Labels } from "../../../../util/Translations"

import PlaceholderConfigWidget from "./PlaceholderConfigWidget"
import DraggableWidget from "./DraggableWidget"

import "./SelectedWidgets.scss"
import { IGrid } from "../../../../models"

interface Props {
    isLoading: boolean
    grid: IGrid
    reorderTiles: (
        targetIndex: number,
        oldIndex: number,
        reorderedTileId: number,
        targetTileId: number,
    ) => void
    removeTile: (tileId: number) => void
}

interface State {
    clientHeight: number
    clientWidth: number
    clientTop: number
    clientLeft: number
}

export default class SelectedWidgets extends React.Component<Props, State> {
    container: React.RefObject<HTMLDivElement>

    constructor(props: Props) {
        super(props)
        this.container = React.createRef()
        this.state = {
            clientHeight: -1,
            clientWidth: -1,
            clientTop: -1,
            clientLeft: -1,
        }
    }

    render(): JSX.Element {
        const { grid, isLoading, removeTile } = this.props

        const { clientHeight, clientWidth, clientTop, clientLeft } = this.state

        const { SelectedWidgets } = Labels
        return (
            <div className="tw-w-full tw-m-2 bg-white theme-bubble">
                <header className="step-header">
                    <h3 className="tw-my-2">{SelectedWidgets}</h3>
                </header>
                <div className="tw-p-2 tw-relative" ref={this.container}>
                    {!isLoading && (
                        <DragDropContext
                            onDragEnd={this.onDragEnd}
                            onDragStart={this.onDragStart}
                            onDragUpdate={this.onDragUpdate}
                        >
                            <Droppable droppableId="coverPageWidgetsDroppable">
                                {(provided, snapshot): JSX.Element => (
                                    <div
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                    >
                                        {grid.tiles.map((tile, index) => (
                                            <Draggable
                                                key={tile.id}
                                                draggableId={`${tile.id}`}
                                                index={index}
                                            >
                                                {(
                                                    draggableProvided,
                                                    draggableSnapshot,
                                                ): JSX.Element => (
                                                    <div
                                                        {...draggableProvided.draggableProps}
                                                        ref={
                                                            draggableProvided.innerRef
                                                        }
                                                    >
                                                        <div
                                                            className="draggableThumb"
                                                            {...draggableProvided.dragHandleProps}
                                                        >
                                                            <DraggableWidget
                                                                tile={tile}
                                                                isDragging={
                                                                    draggableSnapshot.isDragging
                                                                }
                                                                removeTile={
                                                                    removeTile
                                                                }
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}

                                        {provided.placeholder}
                                        {clientHeight >= 0 &&
                                            clientWidth >= 0 &&
                                            clientLeft >= 0 &&
                                            snapshot.isDraggingOver && (
                                                <div
                                                    className="draggableThumb tw-absolute tw-mx-2"
                                                    style={{
                                                        top: clientTop,
                                                        left: clientLeft,
                                                        width: clientWidth,
                                                    }}
                                                >
                                                    <PlaceholderConfigWidget
                                                        style={{
                                                            height: clientHeight,
                                                        }}
                                                    />
                                                </div>
                                            )}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                    )}
                </div>
            </div>
        )
    }

    getDraggedDOM = (draggableId: string): Element | null => {
        const domQuery = `[data-rbd-draggable-id='${draggableId}']`
        return document.querySelector(domQuery)
    }

    onDragStart = ({ draggableId, source }: DragStart): void => {
        const draggedDOM = this.getDraggedDOM(draggableId)
        if (!draggedDOM) {
            return
        }

        const containerPaddingTop = this.container?.current
            ? parseFloat(
                  window.getComputedStyle(this.container?.current)
                      .paddingBottom,
              )
            : 0

        const thumbnailChild = draggedDOM?.firstElementChild
        const clientHeight = !!thumbnailChild
            ? thumbnailChild.clientHeight -
              parseFloat(
                  window.getComputedStyle(thumbnailChild).paddingBottom,
              ) -
              parseFloat(window.getComputedStyle(thumbnailChild).paddingTop)
            : 0

        const parentElement = draggedDOM?.parentElement

        const clientLeft = !!parentElement
            ? parseFloat(window.getComputedStyle(parentElement).paddingLeft)
            : 0

        const parentTop = !!parentElement
            ? parseFloat(window.getComputedStyle(parentElement).paddingTop)
            : 0

        const childrenArray: Element[] = !!draggedDOM?.parentNode
            ? [].slice.call(draggedDOM?.parentNode.children)
            : []

        const sourceIndex = source.index

        const clientTop =
            containerPaddingTop +
            parentTop +
            [...childrenArray]
                .slice(0, sourceIndex)
                .reduce((total, current) => {
                    return total + current.clientHeight
                }, 0)

        this.setState({
            clientHeight,
            clientWidth: thumbnailChild?.firstElementChild?.clientWidth ?? 0,
            clientTop,
            clientLeft,
        })
    }

    onDragUpdate = ({ draggableId, source, destination }: DragUpdate): void => {
        const draggedDOM = this.getDraggedDOM(draggableId)
        if (!destination || !draggedDOM) {
            return
        }

        const containerPaddingTop = this.container?.current
            ? parseFloat(
                  window.getComputedStyle(this.container?.current)
                      .paddingBottom,
              )
            : 0

        const parentElement = draggedDOM?.parentElement
        const clientLeft = !!parentElement
            ? parseFloat(window.getComputedStyle(parentElement).paddingLeft)
            : 0

        const parentTop = !!parentElement
            ? parseFloat(window.getComputedStyle(parentElement).paddingTop)
            : 0

        const destinationIndex = destination.index
        const sourceIndex = source.index

        const childrenArray: Element[] = !!draggedDOM?.parentNode
            ? [].slice.call(draggedDOM?.parentNode.children)
            : []
        const movedItem = childrenArray[sourceIndex]
        childrenArray.splice(sourceIndex, 1)

        const updatedArray = [
            ...childrenArray.slice(0, destinationIndex),
            movedItem,
            ...childrenArray.slice(destinationIndex + 1),
        ]

        const clientTop =
            containerPaddingTop +
            parentTop +
            updatedArray.slice(0, destinationIndex).reduce((total, current) => {
                return total + current.clientHeight
            }, 0)

        this.setState({
            clientTop,
            clientLeft,
        })
    }

    onDragEnd = (result: DropResult): void => {
        const {
            grid: { tiles },
        } = this.props
        const { destination, source } = result

        if (!destination) {
            return
        }

        this.setState({
            clientHeight: -1,
            clientWidth: -1,
            clientTop: -1,
            clientLeft: -1,
        })

        const { reorderTiles } = this.props
        reorderTiles(
            destination.index,
            source.index,
            tiles[source.index].id,
            tiles[destination.index].id,
        )
    }
}
